import classNames from "classnames"
import React, { useState, useEffect, useRef, useCallback } from "react"

import NumberDisplay from "./NumberDisplay"
import Typography from "./Typography"
import { Color } from "../../../constants/V2/color"

import { getBackgroundColorClass } from "@utils/V2/color"

type SliderInputProps = {
  steps: number
  value: number
  minValue: number
  maxValue: number
  textColor: Color
  labelText?: string
  onChange?: (value: number) => void
  size?: "default" | "large"
}

const SliderInput = ({
  steps,
  value,
  onChange,
  minValue,
  maxValue,
  labelText,
  textColor,
  size = "default",
}: SliderInputProps) => {
  const isLarge = size === "large"
  const thumbInsetPx = isLarge ? 17 : 14
  const progressRef = useRef(null)
  const [amountValue, setAmountValue] = useState<number>(value)

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value =
        Math.ceil(Number.parseInt(event.target.value) / steps) * steps
      setAmountValue(value)
      onChange && onChange(value)
    },
    [onChange, steps]
  )

  useEffect(() => {
    setAmountValue(value)
  }, [value])

  return (
    <div className="flex flex-col gap-8 md:gap-16 lg:gap-20">
      <div>
        {isLarge && labelText && (
          <Typography
            font="grotesk"
            weight="medium"
            size="body-md"
            color={textColor}
            text={labelText}
          />
        )}
        <NumberDisplay
          type="price"
          size="large"
          valueFontSize={isLarge ? "h3" : undefined}
          value={amountValue}
          adornmentText="R"
          superscriptText={isLarge ? undefined : labelText}
          textColor={textColor}
          strikeSuperscriptText={false}
          adornmentTextPosition="left"
          enableThousandSeparator={true}
        />
      </div>
      <div
        className={classNames(
          "relative mt-20",
          isLarge ? "sm:mt-12" : "sm:mt-0"
        )}
      >
        <div
          style={{
            width: `${
              ((amountValue - minValue) / (maxValue - minValue)) * 100
            }%`,
            maxWidth: `calc(100% - ${thumbInsetPx}px)`,
          }}
          className={classNames(
            "absolute left-0 z-10 w-full -translate-y-full border-r-0",
            isLarge
              ? "top-[3px] h-14 min-w-[17px] rounded-l-12 border-[3px] border-blue/40 bg-clip-padding"
              : "top-[2px] h-8 min-w-[14px] rounded-l-[11px] border-2 border-light-blue",
            getBackgroundColorClass(Color.Blue)
          )}
          ref={progressRef}
        />
        <div
          className={classNames(
            "absolute left-0 z-0 w-full -translate-y-full",
            isLarge
              ? "h-8 rounded-12 border border-charcoal/10 bg-charcoal/10"
              : classNames(
                  "h-[3px] rounded-[11px] opacity-20",
                  getBackgroundColorClass(Color.Black)
                )
          )}
        />
        <input
          type="range"
          step={steps}
          min={minValue}
          max={maxValue}
          value={amountValue}
          id="rangeInputSlider"
          onChange={handleInputChange}
          className={classNames(
            "absolute top-0 z-20 w-full appearance-none bg-transparent",
            isLarge
              ? [
                  "[&::-moz-range-thumb]:box-border [&::-moz-range-thumb]:size-24 [&::-moz-range-thumb]:-translate-y-[70%] [&::-moz-range-thumb]:cursor-grab [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-[3px] [&::-moz-range-thumb]:border-solid [&::-moz-range-thumb]:border-blue [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:ring-[3px] [&::-moz-range-thumb]:ring-blue/40",
                  "[&::-webkit-slider-thumb]:-mt-[17px] [&::-webkit-slider-thumb]:box-border [&::-webkit-slider-thumb]:size-24 [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-[3px] [&::-webkit-slider-thumb]:border-solid [&::-webkit-slider-thumb]:border-blue [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:ring-[3px] [&::-webkit-slider-thumb]:ring-blue/40",
                ]
              : [
                  "[&::-moz-range-thumb]:box-border [&::-moz-range-thumb]:size-20 [&::-moz-range-thumb]:-translate-y-[70%] [&::-moz-range-thumb]:cursor-grab [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-solid [&::-moz-range-thumb]:border-blue [&::-moz-range-thumb]:bg-white",
                  "[&::-webkit-slider-thumb]:-mt-[11px] [&::-webkit-slider-thumb]:box-border [&::-webkit-slider-thumb]:size-20 [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-solid [&::-webkit-slider-thumb]:border-blue [&::-webkit-slider-thumb]:bg-white",
                ]
          )}
        />
      </div>
    </div>
  )
}

export default SliderInput
